import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { Lock as LockIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface ResetPasswordPageProps {
  onBackToLogin: () => void;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ onBackToLogin }) => {
  const [token, setToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loginSettings, setLoginSettings] = useState<{ logoPath?: string; topLogoPath?: string; bottomLogoPath?: string; topLogoHeight?: number }>({});
  const isInviteFlow = new URLSearchParams(window.location.search).get('mode') === 'invite';

  // Hämta token från URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, []);

  // Hämta login-inställningar för logotyper
  useEffect(() => {
    const fetchLoginSettings = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/login-settings`);
        setLoginSettings(response.data);
      } catch (error) {
        console.error('Error fetching login settings:', error);
      }
    };
    fetchLoginSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Ogiltig återställningslänk. Token saknas.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Lösenordet måste vara minst 8 tecken.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
        token,
        newPassword
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ett fel uppstod. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  // Om inget token finns
  if (!token) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#072f53',
        p: 3
      }}>
        <Box sx={{ 
          maxWidth: 400, 
          width: '100%', 
          textAlign: 'center',
          color: 'white'
        }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Ogiltig återställningslänk. Token saknas i URL:en.
          </Alert>
          <Button 
            variant="contained" 
            onClick={onBackToLogin}
            sx={{ 
              backgroundColor: 'white', 
              color: '#072f53',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
          >
            Tillbaka till inloggning
          </Button>
        </Box>
      </Box>
    );
  }

  // Framgångsvy
  if (success) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#072f53',
        p: 3
      }}>
        <Box sx={{ 
          maxWidth: 400, 
          width: '100%', 
          textAlign: 'center',
          color: 'white'
        }}>
          {/* Logotyp */}
          {(loginSettings.topLogoPath || loginSettings.logoPath) && (
            <Box sx={{ mb: 4 }}>
              <img
                src={loginSettings.topLogoPath || loginSettings.logoPath}
                alt="Logo"
                style={{ maxHeight: loginSettings.topLogoHeight || 80, width: 'auto' }}
              />
            </Box>
          )}

          <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {isInviteFlow ? 'Lösenord skapat!' : 'Lösenord återställt!'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
            Ditt lösenord har ändrats. Du kan nu logga in med ditt nya lösenord.
          </Typography>
          <Button 
            variant="contained" 
            onClick={onBackToLogin}
            sx={{ 
              backgroundColor: 'white', 
              color: '#072f53',
              '&:hover': { backgroundColor: '#f0f0f0' },
              py: 1.5,
              px: 4
            }}
          >
            Gå till inloggning
          </Button>
        </Box>
      </Box>
    );
  }

  // Formulär för att ange nytt lösenord
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#072f53',
      p: 3
    }}>
      <Box sx={{ 
        maxWidth: 450, 
        width: '100%',
        color: 'white'
      }}>
        {/* Logotyp */}
        {(loginSettings.topLogoPath || loginSettings.logoPath) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <img
              src={loginSettings.topLogoPath || loginSettings.logoPath}
              alt="Logo"
              style={{ maxHeight: loginSettings.topLogoHeight || 80, width: 'auto' }}
            />
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <LockIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
          <Typography variant="h5" gutterBottom>
            Skapa nytt lösenord
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Ange ditt nya lösenord nedan
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="password"
            label="Nytt lösenord"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
            sx={{ 
              mb: 2, 
              borderRadius: 1,
              '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
              '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5, borderRadius: 0.5 }
            }}
            helperText="Minst 8 tecken"
            FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
          />
          
          <TextField
            fullWidth
            type="password"
            label="Bekräfta lösenord"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            sx={{ 
              mb: 3, 
              borderRadius: 1,
              '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
              '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5, borderRadius: 0.5 }
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ 
              backgroundColor: 'white', 
              color: '#072f53',
              '&:hover': { backgroundColor: '#f0f0f0' },
              py: 1.5,
              fontWeight: 600
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: '#072f53' }} /> : (isInviteFlow ? 'Skapa lösenord' : 'Återställ lösenord')}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button 
              onClick={onBackToLogin}
              sx={{ color: 'white', textDecoration: 'underline' }}
            >
              Tillbaka till inloggning
            </Button>
          </Box>
        </Box>

        {/* Nedre logotyp */}
        {loginSettings.bottomLogoPath && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <img
              src={loginSettings.bottomLogoPath}
              alt="Bottom Logo"
              style={{ maxHeight: 60, width: 'auto', opacity: 0.95 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;













